import clsx from 'clsx';

import { GAME_NUMBERS, GAME_SCORE_MAX } from '@/components/constants/constants';
import { MatchRetireDropdown } from '@/components/molecules';
import { MatchupStatusEnum } from '@/domain/constants/constants';
import { GameScore } from '@/entities/score/GameScore';

interface Props {
	gameNumber: string;
	team: 'away' | 'home';
	isLocked?: boolean;
	onChange: (gameNumber: number, team: 'away' | 'home', gameScore: GameScore) => void;
	matchWinner: boolean;
	matchupStatus?: MatchupStatusEnum;
	value: GameScore;
	showDropdown?: boolean;
	onRetireOrForfeitSelect?: (side: 'away' | 'home', type: 'forfeit' | 'retire' | 'undo', gameNumber: number) => void;
	isGameWinner: boolean;
}

const dropdownOptions = [
	{ value: 'undo', label: 'None' },
	{ value: 'retire', label: 'Retire' },
	{ value: 'forfeit', label: 'Forfeit' }
];

export const ScoreInput = ({
	value,
	onChange,
	isLocked = false,
	gameNumber,
	matchupStatus,
	matchWinner,
	team,
	showDropdown = true,
	onRetireOrForfeitSelect,
	isGameWinner
}: Props) => {
	const isShowDropdown = !isLocked && gameNumber !== GAME_NUMBERS.FINAL && showDropdown;
	const isMatchWinner = value.score && value.score > 0 && matchWinner && matchupStatus !== MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS;
	const isFinalGame = gameNumber === GAME_NUMBERS.FINAL && matchupStatus !== MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS;

	// check is this needed at all
	const handleBlur = () => {
		const game = Number(gameNumber);
		if (value.score === null) {
			onChange(game, team, { ...value, score: 0 });
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		const game = Number(gameNumber);
		const isDigit = /^[0-9]$/.test(e.key);
		const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];

		if (allowedKeys.includes(e.key)) return;

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			const newScore = Math.min((value.score ?? 0) + 1, GAME_SCORE_MAX);
			onChange(game, team, { ...value, score: newScore });
			return;
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			const newScore = Math.max((value.score ?? 0) - 1, 0);
			onChange(game, team, { ...value, score: newScore });
			return;
		}

		// Handle digit input
		if (isDigit) {
			const input = e.target as HTMLInputElement;
			const hasSelection = input.selectionStart !== input.selectionEnd;

			// If text is selected, replace it with the new digit
			if (hasSelection) {
				e.preventDefault();
				const newScore = parseInt(e.key, 10);
				if (newScore <= GAME_SCORE_MAX) {
					onChange(game, team, { ...value, score: newScore });
				}
				return;
			}

			// If no selection, check if we can add the digit
			const currentVal = value.score === null ? '' : String(value.score);
			if (currentVal.length >= 2) {
				e.preventDefault();
			}
		} else {
			// Not a digit, prevent input
			e.preventDefault();
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const game = Number(gameNumber);
		let val = e.target.value;

		if (val === '') {
			onChange(game, team, { ...value, score: 0 });
			return;
		}

		if (!/^\d{1,2}$/.test(val)) return;

		if (val.length > 1 && val.startsWith('0')) {
			val = val.replace(/^0+/, '') || '0';
		}

		const parsed = parseInt(val, 10);
		if (parsed <= GAME_SCORE_MAX) {
			onChange(game, team, { ...value, score: parsed });
		}
	};

	return (
		<div className="flex w-full flex-col items-center font-medium sm:w-auto">
			<div className="mb-2 flex w-full items-center justify-center gap-1">
				{!isShowDropdown && (
					<span className={clsx('text-sm text-gray-900', isLocked && 'text-gray-600')}>
						{gameNumber !== GAME_NUMBERS.FINAL ? `G${gameNumber}` : gameNumber}
					</span>
				)}
				{isShowDropdown ? (
					<MatchRetireDropdown
						options={dropdownOptions}
						onSelect={(value) => {
							onRetireOrForfeitSelect ? onRetireOrForfeitSelect(team, value as 'retire' | 'forfeit' | 'undo', Number(gameNumber)) : {};
						}}
						placeholder={`G${gameNumber}`}
					/>
				) : null}
			</div>
			<input
				type="text"
				value={value.score === null ? '' : String(value.score)}
				inputMode="numeric"
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				onChange={handleChange}
				disabled={isLocked}
				className={clsx(
					'w-full rounded-lg border py-2 text-center text-base shadow-xs sm:size-12',
					'[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
					'focus:outline-none focus:ring-2 focus:ring-warning-400',
					isLocked ? 'border-gray-300 bg-gray-200' : 'border-gray-300 bg-white',
					(value.isRetired || value.isForfeited) &&
						'border-warning-500 text-warning-500 focus:border-warning-500 focus:ring-2 focus:ring-warning-500',
					isFinalGame && 'text-lg font-bold',
					isMatchWinner && 'bg-green-600 text-white border-green-600',
					isGameWinner && isLocked && 'bg-green-light-50 text-success-600 border-success-600',
					!isGameWinner && isLocked && !isMatchWinner && 'bg-white text-gray-600 border-gray-300'
				)}
			/>
			{value.isRetired || value.isForfeited ? <p className="text-sm text-warning-500">{value.isRetired ? 'RET' : 'FF'}</p> : null}
		</div>
	);
};
